// ============================================
// FILE SYSTEM ACCESS SERVICE
// Two-way sync: save files to folder AND read them back
// ============================================
import { dataUrlToBlob } from '../utils/data-url-to-blob';
import {
    getFolderAccessUnsupportedMessage,
    hasDirectoryPicker,
    normaliseDirectoryPickerError,
} from './folder-access-support';
import { isTransientFileSystemError } from './file-system-errors.js';

// Back-compat alias: file-system.js historically named this
// `isTransientFileSystemWriteError`. Internal callers below keep the old name;
// the alias lets us reroute to the consolidated module without churning the
// 5+ call sites in this file.
const isTransientFileSystemWriteError = isTransientFileSystemError;

export const FileSystemService = {
    directoryHandle: null as any,

    // Check if browser supports File System Access API
    isSupported() {
        return hasDirectoryPicker();
    },

    // Read permission is sufficient for reconnecting and pipeline evidence.
    // Explicit user-triggered writers call this with `readwrite` immediately
    // before creating or replacing files.
    async ensureDirectoryPermission(handle: any, mode: 'read' | 'readwrite' = 'read') {
        if (!handle) return false;
        const options = { mode };
        try {
            if (typeof handle.queryPermission === 'function') {
                const current = await handle.queryPermission(options);
                if (current === 'granted') return true;
                if (current === 'denied') return false;
            }
            if (typeof handle.requestPermission === 'function') {
                return await handle.requestPermission(options) === 'granted';
            }
            // Older File System Access implementations expose writable handles
            // without the permission methods. Preserve their best-effort path.
            return true;
        } catch {
            return false;
        }
    },

    // Pick a new project folder
    async pickDirectory() {
        if (!this.isSupported()) {
            throw new Error(getFolderAccessUnsupportedMessage('Project folder access'));
        }
        try {
            this.directoryHandle = await (window as any).showDirectoryPicker({
                mode: 'read',
                startIn: 'documents'
            });
            return {
                name: this.directoryHandle.name,
                handle: this.directoryHandle
            };
        } catch (err: any) {
            if (err.name === 'AbortError') {
                return null; // User cancelled
            }
            throw normaliseDirectoryPickerError(err, 'Project folder access');
        }
    },

    // Set directory handle (for reconnecting)
    setDirectoryHandle(handle: any) {
        this.directoryHandle = handle;
    },

    // Request access to a specific folder (for loading saved quotes)
    async requestFolderAccess(expectedFolderName: any) {
        if (!this.isSupported()) {
            throw new Error(getFolderAccessUnsupportedMessage('Reconnect project folder'));
        }
        try {
            this.directoryHandle = await (window as any).showDirectoryPicker({
                mode: 'read',
                startIn: 'documents'
            });
            
            // Verify folder name matches (optional - user can select different folder)
            const selectedName = this.directoryHandle.name;
            const matches = selectedName === expectedFolderName;
            
            return {
                name: selectedName,
                handle: this.directoryHandle,
                matches: matches
            };
        } catch (err: any) {
            if (err.name === 'AbortError') {
                return null; // User cancelled
            }
            throw normaliseDirectoryPickerError(err, 'Reconnect project folder');
        }
    },
    
    // Check if we have a valid directory handle
    hasDirectory() {
        return this.directoryHandle !== null;
    },
    
    // Get current folder name
    getFolderName() {
        return this.directoryHandle?.name || null;
    },
    
    // Write a file to the selected folder
    async writeFile(fileName: any, blob: any) {
        if (!this.directoryHandle) {
            throw new Error('No folder selected. Please select a project folder first.');
        }
        try {
            const fileHandle = await this.directoryHandle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            return { success: true, fileName };
        } catch (err) {
            console.error('Error writing file:', fileName, err);
            throw err;
        }
    },

    // Write text content as a file
    async writeTextFile(fileName: any, content: any) {
        const blob = new Blob([content], { type: 'text/plain' });
        return this.writeFile(fileName, blob);
    },

    // Write JSON data as a file
    async writeJSONFile(fileName: any, data: any) {
        const content = JSON.stringify(data, null, 2);
        const blob = new Blob([content], { type: 'application/json' });
        return this.writeFile(fileName, blob);
    },

    // Read a file from the folder
    async readFile(fileName: any) {
        if (!this.directoryHandle) {
            throw new Error('No folder connected');
        }
        try {
            const fileHandle = await this.directoryHandle.getFileHandle(fileName);
            const file = await fileHandle.getFile();
            return file;
        } catch (err: any) {
            if (err.name === 'NotFoundError') {
                return null; // File doesn't exist
            }
            throw err;
        }
    },

    // Read a photo and return as data URL
    async readPhotoAsDataUrl(fileName: any) {
        const file = await this.readFile(fileName);
        if (!file) return null;

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => resolve(e.target.result);
            reader.onerror = () => reject(new Error('Failed to read photo'));
            reader.readAsDataURL(file);
        });
    },

    // Read a document and return content
    async readDocumentContent(fileName: any) {
        const file = await this.readFile(fileName);
        if (!file) return null;

        // For text files, read as text
        if (fileName.endsWith('.txt') || fileName.endsWith('.json')) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e: any) => resolve(e.target.result);
                reader.onerror = () => reject(new Error('Failed to read document'));
                reader.readAsText(file);
            });
        }

        // For other files, return the file object
        return file;
    },

    // Load all photos from a list of file references
    async loadPhotosFromFolder(photoList: any) {
        if (!this.directoryHandle || !photoList?.length) return [];

        const photos: any[] = [];
        const missing: any[] = [];

        for (const photo of photoList) {
            try {
                const dataUrl = await this.readPhotoAsDataUrl(photo.fileName);
                if (dataUrl) {
                    photos.push({
                        id: 'photo_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                        name: photo.originalName,
                        data: dataUrl
                    });
                } else {
                    missing.push(photo.originalName);
                }
            } catch (err) {
                console.warn('Could not load photo:', photo.fileName, err);
                missing.push(photo.originalName);
            }
        }

        return { photos, missing };
    },

    // Load all documents from a list of file references
    async loadDocumentsFromFolder(docList: any) {
        if (!this.directoryHandle || !docList?.length) return [];

        const documents: any[] = [];
        const missing: any[] = [];

        for (const doc of docList) {
            try {
                const file = await this.readFile(doc.fileName);
                if (file) {
                    // Read content for text files
                    let content: any = '';
                    let pdfDataUrl: any = null;
                    let pdfBlob: any = null;
                    if (doc.fileName.endsWith('.txt')) {
                        content = await this.readDocumentContent(doc.fileName);
                    } else if (/\.pdf$/i.test(doc.fileName) || /\.pdf$/i.test(doc.originalName || '')) {
                        pdfBlob = file.type === 'application/pdf'
                            ? file
                            : file.slice(0, file.size, 'application/pdf');
                        pdfDataUrl = URL.createObjectURL(pdfBlob);
                    }

                    documents.push({
                        id: 'doc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                        name: doc.originalName,
                        type: doc.type || 'other',
                        ext: doc.originalName.split('.').pop().toLowerCase(),
                        content: content,
                        size: file.size,
                        syncFileName: doc.fileName,
                        contentType: pdfDataUrl ? 'application/pdf' : (file.type || undefined),
                        ...(pdfDataUrl ? { pdfDataUrl, pdfBlob } : {}),
                    });
                } else {
                    missing.push(doc.originalName);
                }
            } catch (err) {
                console.warn('Could not load document:', doc.fileName, err);
                missing.push(doc.originalName);
            }
        }

        return { documents, missing };
    },

    // Generate standardized file names
    generatePhotoFileName(index: any, originalName: any) {
        const paddedIndex = String(index).padStart(3, '0');
        const safeName = originalName.replace(/[^a-zA-Z0-9._-]/g, '_');
        return `Photo_${paddedIndex}_${safeName}`;
    },

    generateDocFileName(type: any, originalName: any) {
        const typePrefix = (type || 'other').toUpperCase();
        const safeName = originalName.replace(/[^a-zA-Z0-9._-]/g, '_');
        return `Doc_${typePrefix}_${safeName}`;
    },

    generateDateFileName(prefix: any, extension: any) {
        const date = new Date().toISOString().split('T')[0];
        return `${prefix}_${date}.${extension}`;
    },

    // Save a photo from data URL
    async savePhoto(index: any, originalName: any, dataUrl: any) {
        const fileName = this.generatePhotoFileName(index, originalName);

        // Convert data URL to blob - shared utility, NOT fetch() (CSP-safe)
        const blob = await dataUrlToBlob(dataUrl);

        await this.writeFile(fileName, blob);
        return { fileName, originalName };
    },

    // Save a document
    async saveDocument(type: any, originalName: any, content: any, file: any) {
        const fileName = this.generateDocFileName(type, originalName);

        let blob;
        if (file instanceof Blob || file instanceof File) {
            blob = file;
        } else if (typeof content === 'string') {
            blob = new Blob([content], { type: 'text/plain' });
        } else {
            throw new Error('Invalid document content');
        }

        await this.writeFile(fileName, blob);
        return { fileName, originalName, type };
    },

    // ============================================
    // SUBFOLDER OPERATIONS
    // ============================================

    // Create subfolder in parent directory
    async createSubfolder(parentHandle: any, name: any) {
        if (!parentHandle) {
            throw new Error('No parent folder handle provided');
        }
        try {
            return await parentHandle.getDirectoryHandle(name, { create: true });
        } catch (err) {
            console.error('Error creating subfolder:', name, err);
            throw err;
        }
    },

    // Navigate to existing subfolder
    async openSubfolder(parentHandle: any, name: any) {
        if (!parentHandle) {
            throw new Error('No parent folder handle provided');
        }
        try {
            return await parentHandle.getDirectoryHandle(name);
        } catch (err: any) {
            if (err.name === 'NotFoundError') {
                return null; // Subfolder doesn't exist
            }
            throw err;
        }
    },

    // Write file to specific directory handle (not the default directoryHandle)
    async writeFileToDirectory(directoryHandle: any, fileName: any, blob: any) {
        if (!directoryHandle) {
            throw new Error('No directory handle provided');
        }
        try {
            const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            return { success: true, fileName };
        } catch (err: any) {
            err.fileName = err.fileName || fileName;
            err.qmTransientFileSystem = isTransientFileSystemWriteError(err);
            throw err;
        }
    },

    // Read file from specific directory handle
    async readFileFromDirectory(directoryHandle: any, fileName: any) {
        if (!directoryHandle) {
            throw new Error('No directory handle provided');
        }
        try {
            const fileHandle = await directoryHandle.getFileHandle(fileName);
            return await fileHandle.getFile();
        } catch (err: any) {
            if (err.name === 'NotFoundError') {
                return null; // File doesn't exist
            }
            throw err;
        }
    },

    // Read photo from directory as data URL
    async readPhotoFromDirectory(directoryHandle: any, fileName: any) {
        const file = await this.readFileFromDirectory(directoryHandle, fileName);
        if (!file) return null;

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => resolve(e.target.result);
            reader.onerror = () => reject(new Error('Failed to read photo'));
            reader.readAsDataURL(file);
        });
    },

    // Load all photos from subfolder
    async loadPhotosFromSubfolder(directoryHandle: any, photoList: any) {
        if (!directoryHandle || !photoList?.length) return { photos: [], missing: [] };

        const photos: any[] = [];
        const missing: any[] = [];

        for (const photo of photoList) {
            try {
                const dataUrl = await this.readPhotoFromDirectory(directoryHandle, photo.fileName);
                if (dataUrl) {
                    photos.push({
                        id: 'photo_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                        name: photo.originalName,
                        data: dataUrl
                    });
                } else {
                    missing.push(photo.originalName);
                }
            } catch (err) {
                console.warn('Could not load photo from subfolder:', photo.fileName, err);
                missing.push(photo.originalName);
            }
        }

        return { photos, missing };
    },
    
    // Load all videos from the `videos/` subfolder of the project directory.
    // Enumerates the directory (doesn't depend on a persisted filename list)
    // so it works even if Dexie's projectFolder.files.videos was never
    // populated. Returns video records with fresh blob URLs for playback.
    // Peter 2026-04-19: videos were in the sync folder but not rendering on
    // project reload - the reconnect flow hydrated photos + docs but skipped
    // videos. This closes that gap.
    async loadVideosFromSubfolder(directoryHandle: any) {
        if (!directoryHandle) return { videos: [], missing: [] };
        let videosDir: any;
        try {
            videosDir = await directoryHandle.getDirectoryHandle('videos', { create: false });
        } catch {
            // No videos/ subfolder - nothing to hydrate, not an error
            return { videos: [], missing: [] };
        }
        const videos: any[] = [];
        const missing: any[] = [];
        try {
            for await (const [name, handle] of videosDir.entries()) {
                if (handle.kind !== 'file') continue;
                // Skip transcript sidecars (e.g. Video_1_transcript.txt)
                if (/_transcript\.txt$/i.test(name)) continue;
                // Also skip other obvious non-video files
                if (/\.(json|md|txt)$/i.test(name)) continue;
                try {
                    const file = await handle.getFile();
                    const originalName = name.replace(/^Video_\d+_/, '');
                    videos.push({
                        id: 'vid_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9),
                        name: originalName,
                        syncFileName: name,
                        size: file.size,
                        file,
                        url: URL.createObjectURL(file),
                    });
                } catch (err) {
                    console.warn('Could not read video from folder:', name, err);
                    missing.push(name);
                }
            }
        } catch (err) {
            console.warn('Could not enumerate videos/ subfolder:', err);
        }
        return { videos, missing };
    },

    // Load all documents from subfolder
    async loadDocumentsFromSubfolder(directoryHandle: any, docList: any) {
        if (!directoryHandle || !docList?.length) return { documents: [], missing: [] };

        const documents: any[] = [];
        const missing: any[] = [];

        for (const doc of docList) {
            try {
                const file = await this.readFileFromDirectory(directoryHandle, doc.fileName);
                if (file) {
                    // Read content for text files
                    let content: any = '';
                    let pdfDataUrl: any = null;
                    let pdfBlob: any = null;
                    if (doc.fileName.endsWith('.txt')) {
                        content = await file.text();
                    } else if (/\.pdf$/i.test(doc.fileName) || /\.pdf$/i.test(doc.originalName || '')) {
                        pdfBlob = file.type === 'application/pdf'
                            ? file
                            : file.slice(0, file.size, 'application/pdf');
                        pdfDataUrl = URL.createObjectURL(pdfBlob);
                    }

                    documents.push({
                        id: 'doc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                        name: doc.originalName,
                        type: doc.type || 'other',
                        ext: doc.originalName.split('.').pop().toLowerCase(),
                        content: content,
                        size: file.size,
                        syncFileName: doc.fileName,
                        contentType: pdfDataUrl ? 'application/pdf' : (file.type || undefined),
                        ...(pdfDataUrl ? { pdfDataUrl, pdfBlob } : {}),
                    });
                } else {
                    missing.push(doc.originalName);
                }
            } catch (err) {
                console.warn('Could not load document from subfolder:', doc.fileName, err);
                missing.push(doc.originalName);
            }
        }

        return { documents, missing };
    },

    // Save photo to specific subfolder
    async savePhotoToSubfolder(directoryHandle: any, index: any, originalName: any, dataUrl: any) {
        const fileName = this.generatePhotoFileName(index, originalName);

        // Convert data URL to blob - shared utility, NOT fetch() (CSP-safe)
        const blob = await dataUrlToBlob(dataUrl);

        await this.writeFileToDirectory(directoryHandle, fileName, blob);
        return { fileName, originalName };
    },

    // Save document to specific subfolder
    async saveDocumentToSubfolder(directoryHandle: any, type: any, originalName: any, content: any, file: any) {
        const fileName = this.generateDocFileName(type, originalName);

        let blob;
        if (file instanceof Blob || file instanceof File) {
            blob = file;
        } else if (typeof content === 'string') {
            blob = new Blob([content], { type: 'text/plain' });
        } else {
            throw new Error('Invalid document content');
        }

        await this.writeFileToDirectory(directoryHandle, fileName, blob);
        return { fileName, originalName, type };
    },

    // Write JSON to subfolder
    async writeJSONToSubfolder(directoryHandle: any, fileName: any, data: any) {
        const content = JSON.stringify(data, null, 2);
        const blob = new Blob([content], { type: 'application/json' });
        return await this.writeFileToDirectory(directoryHandle, fileName, blob);
    },
    
    // Clear directory handle (disconnect)
    disconnect() {
        this.directoryHandle = null;
    }
};
